/**
 * §8.11 Flow K / §9.13 — single source of truth for the expert's Stripe
 * Connect readiness, derived from the scene's `expert_connect` carrier. Neither
 * consumer reads the raw fields inline; both delegate here.
 *
 * Two questions, and they treat a MISSING carrier differently:
 *  - `isConnectActive` — "may the expert make an offer?" The Make Offer gate
 *    (`ConnectGateBanner.isConnectBlocked`) is PERMISSIVE on the unknown case: a
 *    scene with no carrier predates the §8.11 gate and renders ungated.
 *  - `isConnectLinked` — "do we have positive evidence Connect is linked?" The
 *    §9.13 Billing tab's account-status line is CONSERVATIVE on the unknown
 *    case: no carrier ⇒ "Pending"; it never claims "Linked" without evidence.
 *
 * Both map to the live Connect-account read (`payouts_enabled` / charges
 * enabled) wired in FE2/FE4 — production reads the account directly.
 */
import type { EngagementScene } from '../../types';

/** Make Offer gate: an absent carrier is treated as active (ungated). */
export function isConnectActive(scene: EngagementScene): boolean {
  const connect = scene.expert_connect;
  if (connect === null || connect === undefined) return true;
  return connect.stripe_connect_account_id !== null && connect.charges_enabled === true;
}

/** Billing account-status display: an absent carrier reads as not linked. */
export function isConnectLinked(scene: EngagementScene): boolean {
  const connect = scene.expert_connect;
  if (connect === null || connect === undefined) return false;
  return connect.stripe_connect_account_id !== null && connect.charges_enabled === true;
}

/**
 * The live Make Offer gate (ENG-5448 / FE2a): may this expert be paid?
 *
 * **Fails closed, unlike `isConnectActive`.** The mock's permissive
 * absent-carrier rule existed so pre-gate fixtures rendered ungated; on live
 * data an absent payout account means the expert has not onboarded, which is
 * exactly the case the gate is for. `payouts_enabled !== true` blocks, and so
 * does a null account or a failed read — the caller passes `undefined` for a
 * query that errored.
 *
 * Belt-and-braces: the server raises
 * `engagement.expert_payouts_not_enabled` on the mutation, so a stale client
 * cannot slip an offer past this.
 */
export function isPayoutReady(
  account: { payouts_enabled?: boolean | null } | null | undefined
): boolean {
  return account?.payouts_enabled === true;
}
